import { useMemo, useState } from 'react';
import {
  ACCESS_CAP_MESSAGE,
  ACCESS_LEVELS,
  ACCESS_LEVEL_LABEL,
  RECORD_RIGHT_KEYS,
  RECORD_RIGHT_LABEL,
  ROLE_LABEL,
  SHAREABLE_ROLES,
  rightsSummary,
  tabLabel,
  type Role,
} from '@ehr/shared';
import { apiErrorText } from '../../lib/api.js';
import { postShare, type AccessPerson } from '../../lib/contactAccess.js';
import { SaModal, SearchIcon, initials, saMessage, type SaMessage } from './SaModal.js';
import {
  aboveCap,
  applyPreset,
  eligibleMembers,
  includedMembers,
  initialDraft,
  ownerIds,
  pickableUsers,
  reviewSummary,
  roleMembers,
  rowLevels,
  savedMessage,
  setAllLevels,
  setAllMembers,
  setTabLevel,
  shareBody,
  shareSteps,
  shareTabs,
  toggleMember,
  toggleRight,
  toggleRole,
  toggleUser,
  uniformLevel,
  whoProblem,
  type ShareCap,
  type ShareChange,
  type ShareDraft,
  type ShareTarget,
} from './shareState.js';

const roleLabel = (r: string) => ROLE_LABEL[r as Role] ?? r;

/**
 * ehr#730 (SHARE_ACCESS_10-30-V11 Share flow): Who → Access per tab → Review, as the note modal.
 * `save` sends it (POST /contacts/access/share); `collect` hands the picks back (New Contact's
 * "Add for myself & share", saved once the contact exists). Changing one existing grant starts at
 * Access per tab for a person, or at Who with the role locked.
 */
export function ShareFlow({
  targets,
  cap,
  people,
  meId,
  change,
  start,
  mode = 'save',
  onCancel,
  onSaved,
  onCollect,
}: {
  targets: ShareTarget[];
  cap: ShareCap;
  people: AccessPerson[];
  meId?: string;
  change?: ShareChange | null;
  start?: Partial<ShareDraft> | null;
  mode?: 'save' | 'collect';
  onCancel: () => void;
  onSaved?: (message: string) => void;
  onCollect?: (draft: ShareDraft, summary: string) => void;
}) {
  const [d, setD] = useState<ShareDraft>(() => initialDraft(targets, cap, change, start));
  const [userQ, setUserQ] = useState('');
  const [roleQ, setRoleQ] = useState('');
  const [memberQ, setMemberQ] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState<SaMessage | null>(null);
  const [whoInvalid, setWhoInvalid] = useState(false);
  const [saving, setSaving] = useState(false);
  const steps = shareSteps(change);
  const cur = steps[d.step];
  const last = d.step === steps.length - 1;
  const tabs = useMemo(() => shareTabs(targets), [targets]);
  const owners = useMemo(() => ownerIds(targets), [targets]);
  const lockedRole = change?.kind === 'role' ? change.role : null;
  const cat0 = targets[0]?.category ?? 'client';

  const next = () => {
    if (cur === 'Who') {
      const p = whoProblem(d, people, targets);
      if (p) {
        setWhoInvalid(true);
        setMsg(saMessage(p, true));
        return;
      }
    }
    setMsg(null);
    setD((x) => ({ ...x, step: x.step + 1 }));
  };
  const back = () => {
    setMsg(null);
    setD((x) => ({ ...x, step: Math.max(0, x.step - 1) }));
  };
  const save = () => {
    if (!change && whoProblem(d, people, targets)) {
      setMsg(saMessage('Pick at least one user or role', true));
      return;
    }
    const summary = reviewSummary(d, targets);
    if (mode === 'collect') {
      onCollect?.(d, summary);
      return;
    }
    setSaving(true);
    postShare(shareBody(d, targets, change))
      .then(() => onSaved?.(savedMessage(d, people, targets, change, roleLabel)))
      .catch((e: unknown) => {
        setSaving(false);
        setMsg(saMessage(apiErrorText(e, 'Could not share. Try again.'), true));
      });
  };

  const sub =
    targets.length === 1
      ? targets[0]!.name
      : `${targets.length} contacts: ${targets.map((t) => t.name).join(', ')}`;

  // ---- Who ----
  const who = () => {
    const users = pickableUsers(people, targets, meId).filter((u) => {
      const q = userQ.trim().toLowerCase();
      return !q || u.name.toLowerCase().includes(q) || roleLabel(u.role).toLowerCase().includes(q);
    });
    const roles = SHAREABLE_ROLES.filter((r) => !lockedRole || r === lockedRole).filter((r) => {
      const q = roleQ.trim().toLowerCase();
      return !q || roleLabel(r).toLowerCase().includes(q);
    });
    const chips = [
      ...d.users.map((id) => {
        const p = people.find((x) => x.id === id);
        return (
          <span key={`u${id}`} className="acs-tag">
            {p?.name ?? 'Someone'}
            {!change && (
              <button
                type="button"
                aria-label={`Remove ${p?.name ?? ''}`}
                onClick={() => setD((x) => toggleUser(x, id))}
              >
                ×
              </button>
            )}
          </span>
        );
      }),
      ...Object.keys(d.roles).map((r) => (
        <span key={`r${r}`} className="acs-tag">
          {roleLabel(r)} · {includedMembers(d, r, people, targets).length} of{' '}
          {eligibleMembers(people, r, targets).length}
          {!lockedRole && (
            <button
              type="button"
              aria-label={`Remove ${roleLabel(r)}`}
              onClick={() => setD((x) => toggleRole(x, r))}
            >
              ×
            </button>
          )}
        </span>
      )),
    ];
    return (
      <>
        <div className="acs-card">
          <div className="acs-seg" role="tablist" aria-label="Share with">
            <button
              type="button"
              className={d.whoTab === 'users' ? 'on' : ''}
              disabled={!!lockedRole}
              onClick={() => setD((x) => ({ ...x, whoTab: 'users' }))}
            >
              Users
            </button>
            <button
              type="button"
              className={d.whoTab === 'roles' ? 'on' : ''}
              disabled={!!lockedRole}
              onClick={() => setD((x) => ({ ...x, whoTab: 'roles' }))}
            >
              Roles
            </button>
          </div>
          {chips.length > 0 && <div className="acs-chips">{chips}</div>}
        </div>
        <div className="acs-search">
          <SearchIcon />
          <input
            placeholder={d.whoTab === 'users' ? 'Search users' : 'Search roles'}
            aria-label={d.whoTab === 'users' ? 'Search users' : 'Search roles'}
            value={d.whoTab === 'users' ? userQ : roleQ}
            onChange={(e) => (d.whoTab === 'users' ? setUserQ : setRoleQ)(e.target.value)}
          />
        </div>
        <div className={`acs-list${whoInvalid ? ' invalid' : ''}`} id="acs-who-list">
          {d.whoTab === 'users' ? (
            users.length ? (
              users.map((u) => {
                const on = d.users.includes(u.id);
                const pick = () => {
                  setWhoInvalid(false);
                  setD((x) => toggleUser(x, u.id));
                };
                return (
                  <div key={u.id} className={`acs-row${on ? ' on' : ''}`} onClick={pick}>
                    <input
                      type="checkbox"
                      checked={on}
                      aria-label={u.name}
                      onClick={(e) => e.stopPropagation()}
                      onChange={pick}
                    />
                    <span className="acs-avatar">{initials(u.name)}</span>
                    <div>
                      <div className="acs-nm">{u.name}</div>
                      <div className="acs-rl">{roleLabel(u.role)}</div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="acs-empty">No users match</div>
            )
          ) : roles.length ? (
            roles.map((r) => {
              const on = !!d.roles[r];
              const members = roleMembers(people, r);
              const mq = (memberQ[r] ?? '').trim().toLowerCase();
              const pick = () => {
                if (lockedRole) return;
                setWhoInvalid(false);
                setD((x) => toggleRole(x, r));
              };
              return (
                <div key={r}>
                  <div className={`acs-row${on ? ' on' : ''}`} onClick={pick}>
                    <input
                      type="checkbox"
                      checked={on}
                      disabled={!!lockedRole}
                      aria-label={roleLabel(r)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={pick}
                    />
                    <span className="acs-avatar">{initials(roleLabel(r))}</span>
                    <div>
                      <div className="acs-nm">{roleLabel(r)}</div>
                      <div className="acs-rl">
                        {on
                          ? `${includedMembers(d, r, people, targets).length} of ${eligibleMembers(people, r, targets).length} selected`
                          : `${members.length} member${members.length === 1 ? '' : 's'}`}
                      </div>
                    </div>
                  </div>
                  {on && (
                    <>
                      <div className="acs-role-tools">
                        <div className="acs-search">
                          <SearchIcon />
                          <input
                            placeholder={`Search within ${roleLabel(r)}`}
                            aria-label={`Search within ${roleLabel(r)}`}
                            value={memberQ[r] ?? ''}
                            onChange={(e) => setMemberQ((m) => ({ ...m, [r]: e.target.value }))}
                          />
                        </div>
                        <button
                          type="button"
                          className="acs-link"
                          onClick={() => setD((x) => setAllMembers(x, r, true, people))}
                        >
                          Select all
                        </button>
                        <button
                          type="button"
                          className="acs-link"
                          onClick={() => setD((x) => setAllMembers(x, r, false, people))}
                        >
                          Clear
                        </button>
                      </div>
                      {members
                        .filter((u) => !mq || u.name.toLowerCase().includes(mq))
                        .map((u) => {
                          const isOwner = owners.has(u.id);
                          const inc = !isOwner && !(d.roles[r] ?? []).includes(u.id);
                          const tick = () => !isOwner && setD((x) => toggleMember(x, r, u.id));
                          return (
                            <div
                              key={u.id}
                              className={`acs-row member${inc ? ' on' : ''}${isOwner ? ' locked' : ''}`}
                              onClick={tick}
                            >
                              <input
                                type="checkbox"
                                checked={inc}
                                disabled={isOwner}
                                aria-label={u.name}
                                onClick={(e) => e.stopPropagation()}
                                onChange={tick}
                              />
                              <span className="acs-avatar sm">{initials(u.name)}</span>
                              <div className="acs-nm">
                                {u.name}
                                {u.id === meId && <span className="acs-muted"> (you)</span>}
                              </div>
                              {isOwner && <span className="acs-pill acs-ml-auto">Owner</span>}
                            </div>
                          );
                        })}
                      {members.length === 0 && (
                        <div className="acs-row member locked">No one holds this role yet</div>
                      )}
                    </>
                  )}
                </div>
              );
            })
          ) : (
            <div className="acs-empty">No roles match</div>
          )}
        </div>
        <p className="acs-muted" style={{ margin: '10px 2px 0' }}>
          Admins and managers open every contact already.
        </p>
      </>
    );
  };

  // ---- Access per tab ----
  const accessStep = () => {
    const uni = uniformLevel(d, targets);
    return (
      <>
        <div className="acs-presets">
          <span className="acs-label">Presets</span>
          <button
            type="button"
            className="btn sm"
            onClick={() => setD((x) => applyPreset(x, targets, 'view', cap))}
          >
            View only
          </button>
          <button
            type="button"
            className="btn sm"
            onClick={() => setD((x) => applyPreset(x, targets, 'edit', cap))}
          >
            Edit all
          </button>
          <button
            type="button"
            className="btn sm"
            onClick={() => setD((x) => applyPreset(x, targets, 'full', cap))}
          >
            Full Access
          </button>
        </div>
        <table className="acs-table acc" id="acs-acc-table">
          <thead>
            <tr>
              <th>Tab</th>
              <th>Access</th>
            </tr>
          </thead>
          <tbody>
            <tr className="all">
              <td className="tabname">All tabs</td>
              <td>
                <div className="acs-seg sm" aria-label="All tabs">
                  {ACCESS_LEVELS.map((l) => (
                    <button
                      key={l}
                      type="button"
                      className={uni === l ? 'on' : ''}
                      onClick={() => setD((x) => setAllLevels(x, targets, l, cap))}
                    >
                      {ACCESS_LEVEL_LABEL[l]}
                    </button>
                  ))}
                </div>
              </td>
            </tr>
            {tabs.map((t) => (
              <tr key={t.key} data-tab-row={t.key}>
                <td className="tabname">{tabLabel(t, cat0)}</td>
                <td>
                  <div className="acs-seg sm" aria-label={`${tabLabel(t, cat0)} access`}>
                    {rowLevels(t).map((l) => {
                      const capped = aboveCap(cap, t, l);
                      return (
                        <button
                          key={l}
                          type="button"
                          className={d.tabs[t.key] === l ? 'on' : ''}
                          disabled={capped}
                          title={capped ? ACCESS_CAP_MESSAGE : undefined}
                          onClick={() => setD((x) => setTabLevel(x, t, l, cap))}
                        >
                          {ACCESS_LEVEL_LABEL[l]}
                        </button>
                      );
                    })}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="acs-label acs-gap" style={{ marginBottom: 8 }}>
          Record permissions
        </div>
        <div className="acs-rights">
          {RECORD_RIGHT_KEYS.map((k) => (
            <div key={k} className="acs-right-card">
              <span>{RECORD_RIGHT_LABEL[k]}</span>
              <button
                type="button"
                role="switch"
                className={`acs-switch${d.rights[k] ? ' on' : ''}`}
                aria-checked={d.rights[k]}
                aria-label={RECORD_RIGHT_LABEL[k]}
                disabled={!cap.rights[k]}
                title={cap.rights[k] ? undefined : ACCESS_CAP_MESSAGE}
                onClick={() => setD((x) => toggleRight(x, k, cap))}
              />
            </div>
          ))}
        </div>
      </>
    );
  };

  // ---- Review ----
  const review = () => {
    const summary = reviewSummary(d, targets);
    const rights = rightsSummary(d.rights);
    return (
      <>
        <div className="acs-card" style={{ marginBottom: 12 }}>
          <div className="acs-label">{targets.length === 1 ? 'Contact' : 'Contacts'}</div>
          <div className="acs-nm" style={{ marginTop: 4 }}>
            {mode === 'collect' ? 'The new contact' : targets.map((t) => t.name).join(', ')}
          </div>
        </div>
        <table className="acs-table list" id="acs-review-table">
          <thead>
            <tr>
              <th>Recipient</th>
              <th className="acs-hide-m">Role</th>
              <th>Access</th>
              <th className="acs-hide-m">Record permissions</th>
            </tr>
          </thead>
          <tbody>
            {d.users.map((id) => {
              const p = people.find((x) => x.id === id);
              return (
                <tr key={`u${id}`}>
                  <td>
                    <div className="acs-flex">
                      <span className="acs-avatar sm">{initials(p?.name ?? '?')}</span>
                      <span className="acs-nm">{p?.name ?? 'Someone'}</span>
                    </div>
                  </td>
                  <td className="acs-hide-m">{roleLabel(p?.role ?? '')}</td>
                  <td>{summary}</td>
                  <td className="acs-hide-m">{rights}</td>
                </tr>
              );
            })}
            {Object.keys(d.roles).map((r) => {
              const inc = includedMembers(d, r, people, targets);
              const out = roleMembers(people, r).filter((u) => (d.roles[r] ?? []).includes(u.id));
              return (
                <tr key={`r${r}`}>
                  <td>
                    <div className="acs-nm">{roleLabel(r)}</div>
                    <div className="acs-chips" style={{ marginTop: 6 }}>
                      <span className="acs-tag">Role</span>
                      <span className="acs-tag">
                        {inc.length} of {eligibleMembers(people, r, targets).length} members
                      </span>
                      {out.length > 0 && (
                        <span className="acs-tag">
                          Not included: {out.map((u) => u.name).join(', ')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="acs-hide-m">Role</td>
                  <td>{summary}</td>
                  <td className="acs-hide-m">{rights}</td>
                </tr>
              );
            })}
            {d.users.length + Object.keys(d.roles).length === 0 && (
              <tr>
                <td colSpan={4} className="acs-empty">
                  No one picked yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="acs-card acs-gap">
          <label className="acs-check">
            <input
              type="checkbox"
              checked={d.notify}
              onChange={(e) => setD((x) => ({ ...x, notify: e.target.checked }))}
            />
            Notify them
          </label>
        </div>
      </>
    );
  };

  return (
    <SaModal
      title={change ? 'Change access' : 'Share'}
      sub={mode === 'collect' ? 'The new contact' : sub}
      onClose={onCancel}
      msg={msg}
      onMsgDone={() => setMsg(null)}
      scrollKey={d.step}
      foot={{
        left:
          d.step > 0 ? (
            <button type="button" className="btn" onClick={back}>
              ← Back
            </button>
          ) : null,
        right: (
          <>
            <button type="button" className="btn" onClick={onCancel}>
              Cancel
            </button>
            {last ? (
              <button
                type="button"
                className="btn primary"
                onClick={save}
                disabled={saving}
                id="acs-share-save"
              >
                {saving
                  ? 'Saving…'
                  : change
                    ? 'Update access'
                    : mode === 'collect'
                      ? 'Done'
                      : 'Share'}
              </button>
            ) : (
              <button type="button" className="btn primary" onClick={next} id="acs-share-next">
                Next
              </button>
            )}
          </>
        ),
      }}
    >
      <div className="acs-steps">
        {steps.map((s, i) => (
          <span key={s} style={{ display: 'contents' }}>
            <span className={`acs-step${i === d.step ? ' on' : i < d.step ? ' done' : ''}`}>
              <span className="n">{i < d.step ? '✓' : i + 1}</span>
              <span className="lbl">{s}</span>
            </span>
            {i < steps.length - 1 && <span className="acs-step-line" />}
          </span>
        ))}
      </div>
      {cur === 'Who' ? who() : cur === 'Access per tab' ? accessStep() : review()}
    </SaModal>
  );
}
